import React, { useEffect, useRef, useState } from "react";
import "./ScrollFilmBackdrop.css";

/* ScrollFilmBackdrop
   ----------------------------------------------------------------------
   The home page's scroll-scrubbed film: 240 frames (24 fps × 10 s of
   the logo-assembly film) drawn on a fixed canvas behind the page,
   advancing as the visitor scrolls from the hero to the end of Trips.

   SMOOTHNESS (rewrite 2026-09-29, owner: "the bg movement is not
   smooth, it needs to be super smooth"). Measured on an M1 before the
   rewrite: a mouse-wheel notch jumped the film 7 frames at once (36
   paints for the whole film), and frames that had not been decoded
   yet were decoded on the main thread mid-scroll. Now:

   1. Smoothed playhead. The scroll position sets a target; the drawn
      playhead follows it with a time-based ease (TAU), so a wheel
      notch becomes a glide instead of a jump. Nothing ever snaps.
   2. Sub-frame blending. The playhead is fractional; frame n is drawn
      and frame n+1 is laid over it at the fractional opacity, so slow
      scrolling reads as continuous motion instead of stepping at the
      film's 24 fps. Never across the hard cut between frames 111 and
      112 (switches at the midpoint there). When scrolling stops the
      playhead settles on a whole frame, so a resting page never shows
      a blend.
   3. Decode off the main thread. Frames are fetched as blobs and
      turned into ImageBitmaps with createImageBitmap, already scaled
      to the size they are drawn at, so each paint is a straight copy.
      Only frames with a ready bitmap are drawn; if the exact frame is
      not ready the nearest ready one stands in for that paint.
   4. No wasted pixels. The canvas backing store is never larger than
      the source footage needs (1920×1080): on a retina laptop it
      drops from 2880×1800 to 1728×1080 with no visible difference,
      because the film was already being upscaled.
   5. No layout reads per frame. The scroll range is measured on load,
      resize and when the page height changes, not on every tick.
   6. The loop only runs while something moves.

   LOADING (keeps Edit 3's rule: never download the film before the
   visitor needs it):
     - before the page's load event: frame 1 only (preloaded in <head>);
     - once the page has loaded and the browser is idle: keyframes
       (every KEYFRAME_STEP) so any position has a nearby stand-in;
     - from the first scroll: a window around the playhead, reaching
       FETCH_AHEAD frames in the direction of travel; frames ahead of
       the visitor come before frames behind them, nearest first.
   Decoded bitmaps are kept for keyframes plus a window around the
   playhead (MAX_BITMAPS); the rest are released and re-decoded from
   the cached blobs if the visitor comes back.

   GLASS. The home page's glass cards blur the film behind them. That
   blur used to switch off while scrolling (body.sfb-scrolling) and
   fade back on stop, which made the cards flicker between frosted and
   clear; it now stays on. If a machine cannot hold the frame rate
   while scrolling (most of the last 45 scroll frames slower than
   SLOW_FRAME_MS), body.sfb-lite drops the blur once for the rest of
   the visit instead of toggling it. body.sfb-film marks that the film
   is running; without it (phones, reduced motion) the cards have no
   blur at all, because blurring the plain gradient changes nothing.

   Hydration: mounts after hydration only; the layer carries
   data-prerender="omit" (see README). */

const FRAME_COUNT = 240;
const FRAME_BASE  = "/videos/frames/f-";
const FRAME_PAD   = 3;
const EXT_WEBP    = ".webp";
const EXT_JPG     = ".jpg";
const SRC_W = 1920;
const SRC_H = 1080;

/* Hard scene cut between these 0-based frame pairs (n, n+1): never blend. */
const CUTS = new Set([111]);

const KEYFRAME_STEP     = 12;
const FETCH_AHEAD       = 120;  /* half the film, in the direction of travel */
const FETCH_BEHIND      = 24;
const FETCH_CONCURRENCY = 8;
const DECODE_AHEAD      = 30;
const DECODE_BEHIND     = 10;
const DECODE_CONCURRENCY = 3;
/* Decode window (41) + keyframes (21) + headroom. Frames inside the
   window are never evicted, so a frame is decoded once per pass. */
const MAX_BITMAPS       = 72;

const TAU           = 0.1;  /* s: playhead ease time constant */
const SETTLE_MS     = 140;  /* no scroll for this long: settle on a whole frame */
const SLOW_FRAME_MS = 24;

const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));

function pad(n, width) {
  let s = String(n);
  while (s.length < width) s = "0" + s;
  return s;
}

function useReducedMotion() {
  const [r, setR] = useState(
    typeof window !== "undefined" && window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handler = (e) => setR(e.matches);
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, []);
  return r;
}

function useViewportWidth() {
  const [w, setW] = useState(typeof window === "undefined" ? 1024 : window.innerWidth);
  useEffect(() => {
    const onResize = () => setW(window.innerWidth);
    window.addEventListener("resize", onResize, { passive: true });
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return w;
}

export default function ScrollFilmBackdrop(props) {
  /* Mount only after hydration. The pre-rendered HTML never contains
     this layer (scripts/prerender.mjs drops data-prerender="omit"
     elements), and a first client render of null matches that. Frame
     loading and the viewport check below therefore only ever run with
     the real window size, never with a placeholder. */
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);
  if (!mounted) return null;
  return <ScrollFilmBackdropLayer {...props} />;
}

function ScrollFilmBackdropLayer({
  endSectionId,
  poster,
  enableMobile = false,
}) {
  const canvasRef = useRef(null);
  const layerRef  = useRef(null);
  const [ready, setReady]   = useState(false);
  const [failed, setFailed] = useState(false);
  const reduced = useReducedMotion();
  const vw      = useViewportWidth();

  const mobileBlocked = !enableMobile && vw < 768;
  const skip = reduced || mobileBlocked;

  /* body.sfb-on: the home page's glass styling (set on every device,
     round 37). body.sfb-film: the film is actually running, so the
     glass cards have something to blur. */
  useEffect(() => {
    if (failed) return undefined;
    const body = document.body;
    body.classList.add("sfb-on");
    if (!skip) body.classList.add("sfb-film");
    return () => {
      body.classList.remove("sfb-on");
      body.classList.remove("sfb-film");
    };
  }, [failed, skip]);

  useEffect(() => {
    if (skip || failed) return undefined;
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const player = createPlayer({
      canvas,
      layer: layerRef.current,
      endSectionId,
      enableMobile,
      onReady: () => setReady(true),
      onFail: () => setFailed(true),
    });
    return () => player.destroy();
  }, [skip, failed, endSectionId, enableMobile]);

  if (skip || failed) return null;

  return (
    <div
      ref={layerRef}
      className={`sfb${ready ? " sfb--ready" : ""}`}
      aria-hidden="true"
      data-prerender="omit"
      style={{ backgroundImage: poster ? `url(${poster})` : undefined }}
    >
      <canvas ref={canvasRef} className="sfb-canvas" />
    </div>
  );
}

/* ─── The player ──────────────────────────────────────────────────
   Plain closures, outside React: nothing in here re-renders. */
function createPlayer({ canvas, layer, endSectionId, enableMobile, onReady, onFail }) {
  const N = FRAME_COUNT;
  let destroyed = false;
  const ctx = canvas.getContext("2d", { alpha: !!enableMobile });
  ctx.imageSmoothingEnabled = true;

  /* frame data */
  const blobs      = new Array(N).fill(null);
  const fetchState = new Uint8Array(N);          /* 0 none · 1 loading · 2 done · 3 failed */
  const bitmaps    = new Array(N).fill(null);
  const bitmapKey  = new Array(N).fill("");      /* size the bitmap was decoded for */
  const decoding   = new Uint8Array(N);
  const blobExt    = new Array(N).fill("");      /* which extension a blob was fetched as */
  let bitmapCount = 0;
  let inflightFetch = 0;
  let inflightDecode = 0;
  let ext = EXT_WEBP;
  let hardErrors = 0;
  let phase = 1;                                  /* 1 first frame · 2 keyframes · 3 follow the visitor */
  let announcedReady = false;

  /* geometry */
  let backW = 0, backH = 0, drawW = 0, drawH = 0, drawX = 0, drawY = 0, sizeKey = "";
  let contain = false;
  const measureCanvas = () => {
    const cssW = window.innerWidth;
    const cssH = window.innerHeight;
    contain = !!enableMobile && cssW < 768;
    const fit = contain ? Math.min(cssW / SRC_W, cssH / SRC_H) : Math.max(cssW / SRC_W, cssH / SRC_H);
    const dpr = window.devicePixelRatio || 1;
    /* Never render at more pixels than the footage has. */
    const scale = clamp(Math.min(dpr, 2, 1 / fit), 0.5, 2);
    backW = Math.max(1, Math.round(cssW * scale));
    backH = Math.max(1, Math.round(cssH * scale));
    drawW = Math.round(SRC_W * fit * scale);
    drawH = Math.round(SRC_H * fit * scale);
    drawX = Math.round((backW - drawW) / 2);
    drawY = Math.round((backH - drawH) / 2);
    sizeKey = `${drawW}x${drawH}`;
    if (canvas.width !== backW) canvas.width = backW;
    if (canvas.height !== backH) canvas.height = backH;
    ctx.imageSmoothingQuality = "medium";
  };

  /* scroll range, measured only when layout changes */
  let denom = 1;
  let fadeStart = Infinity;
  const measureRange = () => {
    const endEl = document.getElementById(endSectionId);
    if (!endEl) { denom = 1; fadeStart = Infinity; return; }
    const endY = endEl.getBoundingClientRect().bottom + window.scrollY;
    denom = Math.max(1, endY - window.innerHeight);
    fadeStart = endY - window.innerHeight;
  };

  /* playhead */
  const rawFrame = () => clamp(window.scrollY / denom, 0, 1) * (N - 1);
  let p = 0;
  let target = 0;
  let dir = 1;
  let lastScrollT = -1e9;
  let lastTs = 0;
  let rafId = 0;
  let running = false;
  let drawnKey = "";
  let forceDraw = true;
  let lastPumpFrame = -1;
  let lastOpacity = -1;

  /* adaptive quality: one-way switch, never toggles back */
  let lite = document.body.classList.contains("sfb-lite");
  const samples = [];
  const sample = (dtMs) => {
    if (lite) return;
    samples.push(dtMs > SLOW_FRAME_MS ? 1 : 0);
    if (samples.length > 45) samples.shift();
    if (samples.length === 45 && samples.reduce((a, b) => a + b, 0) > 30) {
      lite = true;
      document.body.classList.add("sfb-lite");
    }
  };

  /* ── loading ── */
  const url = (i) => FRAME_BASE + pad(i + 1, FRAME_PAD) + ext;

  const order = () => {
    const cur = clamp(Math.round(p), 0, N - 1);
    const list = [];
    const seen = new Uint8Array(N);
    const push = (i) => { if (i >= 0 && i < N && !seen[i]) { seen[i] = 1; list.push(i); } };
    push(cur);
    push(0);
    if (phase >= 3) {
      for (let d = 1; d <= FETCH_AHEAD; d++) push(cur + dir * d);
      for (let d = 1; d <= FETCH_BEHIND; d++) push(cur - dir * d);
    }
    if (phase >= 2) {
      const keys = [];
      for (let i = 0; i < N; i += KEYFRAME_STEP) keys.push(i);
      keys.push(N - 1);
      keys.sort((a, b) => Math.abs(a - cur) - Math.abs(b - cur));
      keys.forEach(push);
    }
    return list;
  };

  const fetchFrame = (i) => {
    fetchState[i] = 1;
    inflightFetch++;
    const e = ext;
    fetch(url(i))
      .then((r) => { if (!r.ok) throw new Error(String(r.status)); return r.blob(); })
      .then((blob) => {
        if (destroyed) return;
        if (e !== ext) { fetchState[i] = 0; return; }   /* fetched before a format switch */
        blobs[i] = blob;
        blobExt[i] = e;
        fetchState[i] = 2;
      })
      .catch(() => {
        if (destroyed) return;
        if (e !== ext) { fetchState[i] = 0; return; }
        fetchState[i] = 3;
        if (++hardErrors > 12) onFail();
      })
      .finally(() => {
        inflightFetch--;
        if (!destroyed) pump();
      });
  };

  const makeBitmap = (blob) => {
    if (typeof window.createImageBitmap === "function") {
      return window.createImageBitmap(blob, { resizeWidth: drawW, resizeHeight: drawH, resizeQuality: "high" })
        .catch(() => window.createImageBitmap(blob));
    }
    /* Very old browsers: a decoded <img> is drawable too. */
    return new Promise((resolve, reject) => {
      const objectUrl = URL.createObjectURL(blob);
      const img = new Image();
      img.onload = () => {
        const done = () => { URL.revokeObjectURL(objectUrl); resolve(img); };
        if (img.decode) img.decode().then(done, done); else done();
      };
      img.onerror = () => { URL.revokeObjectURL(objectUrl); reject(new Error("decode")); };
      img.src = objectUrl;
    });
  };

  const isKeyframe = (i) => i % KEYFRAME_STEP === 0 || i === N - 1 || i === 0;

  const releaseBitmap = (i) => {
    const bm = bitmaps[i];
    if (!bm) return;
    if (typeof bm.close === "function") bm.close();
    bitmaps[i] = null;
    bitmapKey[i] = "";
    bitmapCount--;
  };

  /* Release bitmaps outside the decode window (farthest first) when
     over budget. Never inside the window: evicting a frame the decoder
     still wants made it decode the same frame again and again. */
  const inDecodeWindow = (i, cur) => {
    const rel = (i - cur) * dir;
    return rel >= -DECODE_BEHIND && rel <= DECODE_AHEAD;
  };
  const evict = () => {
    if (bitmapCount <= MAX_BITMAPS) return;
    const cur = Math.round(p);
    const candidates = [];
    for (let i = 0; i < N; i++) {
      if (bitmaps[i] && !isKeyframe(i) && !inDecodeWindow(i, cur)) candidates.push(i);
    }
    candidates.sort((a, b) => Math.abs(b - cur) - Math.abs(a - cur));
    while (bitmapCount > MAX_BITMAPS && candidates.length) releaseBitmap(candidates.shift());
  };

  const decodeFrame = (i) => {
    decoding[i] = 1;
    inflightDecode++;
    const key = sizeKey;
    makeBitmap(blobs[i])
      .then((bm) => {
        if (destroyed) { if (bm && typeof bm.close === "function") bm.close(); return; }
        if (bitmaps[i]) releaseBitmap(i);
        bitmaps[i] = bm;
        bitmapKey[i] = key;
        bitmapCount++;
        if (!announcedReady) { announcedReady = true; onReady(); }
        if (Math.abs(i - p) <= 2 || drawnKey === "") { forceDraw = true; kick(); }
        evict();
      })
      .catch(() => {
        if (destroyed) return;
        if (blobExt[i] === EXT_WEBP && !announcedReady) {
          /* No WebP decoding here: switch the whole film to the JPEGs. */
          ext = EXT_JPG;
          for (let k = 0; k < N; k++) { blobs[k] = null; blobExt[k] = ""; if (fetchState[k] !== 1) fetchState[k] = 0; }
          return;
        }
        if (blobExt[i] !== ext) { blobs[i] = null; fetchState[i] = 0; return; }
        blobs[i] = null;
        fetchState[i] = 3;
        if (++hardErrors > 12) onFail();
      })
      .finally(() => {
        decoding[i] = 0;
        inflightDecode--;
        if (!destroyed) pump();
      });
  };

  const decodeOrder = () => {
    const cur = clamp(Math.round(p), 0, N - 1);
    const list = [];
    const want = (i) => i >= 0 && i < N && blobs[i] && !decoding[i] && (!bitmaps[i] || bitmapKey[i] !== sizeKey);
    if (want(cur)) list.push(cur);
    for (let d = 1; d <= DECODE_AHEAD; d++) if (want(cur + dir * d)) list.push(cur + dir * d);
    for (let d = 1; d <= DECODE_BEHIND; d++) if (want(cur - dir * d)) list.push(cur - dir * d);
    for (let i = 0; i < N; i++) if (isKeyframe(i) && want(i) && !list.includes(i)) list.push(i);
    return list;
  };

  function pump() {
    if (destroyed) return;
    if (inflightFetch < FETCH_CONCURRENCY) {
      for (const i of order()) {
        if (inflightFetch >= FETCH_CONCURRENCY) break;
        if (fetchState[i] === 0) fetchFrame(i);
      }
    }
    if (inflightDecode < DECODE_CONCURRENCY) {
      for (const i of decodeOrder()) {
        if (inflightDecode >= DECODE_CONCURRENCY) break;
        decodeFrame(i);
      }
    }
  }

  /* ── drawing ── */
  const nearestBitmap = (i) => {
    if (bitmaps[i]) return i;
    for (let d = 1; d < N; d++) {
      if (i - d >= 0 && bitmaps[i - d]) return i - d;
      if (i + d < N && bitmaps[i + d]) return i + d;
    }
    return -1;
  };

  const blit = (src, alpha) => {
    ctx.globalAlpha = alpha;
    ctx.drawImage(src, drawX, drawY, drawW, drawH);
  };

  const draw = () => {
    let a = Math.floor(p);
    let f = p - a;
    if (a >= N - 1) { a = N - 1; f = 0; }
    if (f > 0 && CUTS.has(a)) { if (f >= 0.5) a += 1; f = 0; }
    const q = Math.round(f * 24) / 24;
    if (q >= 1) { a += 1; }
    const A = nearestBitmap(a);
    if (A < 0) return;
    const b = a + 1;
    const B = A === a && q > 0 && q < 1 && b < N && bitmaps[b] ? b : -1;
    const key = B >= 0 ? `${A}+${B}@${q}` : `${A}`;
    if (key === drawnKey && !forceDraw) return;
    drawnKey = key;
    forceDraw = false;
    if (contain) ctx.clearRect(0, 0, backW, backH);
    blit(bitmaps[A], 1);
    if (B >= 0) blit(bitmaps[B], q);
    ctx.globalAlpha = 1;
  };

  const updateOpacity = () => {
    if (!contain || !layer) return;
    const past = window.scrollY - fadeStart;
    const op = past <= 0 ? 1 : past >= 280 ? 0 : 1 - past / 280;
    if (op !== lastOpacity) {
      layer.style.opacity = op === 1 ? "" : String(op);
      lastOpacity = op;
    }
  };

  function tick(ts) {
    running = false;
    if (destroyed) return;
    const dtMs = lastTs ? Math.min(100, ts - lastTs) : 1000 / 60;
    lastTs = ts;
    const scrolling = ts - lastScrollT < SETTLE_MS;
    if (scrolling) sample(dtMs);

    const raw = rawFrame();
    const goal = scrolling ? raw : Math.round(raw);
    if (goal !== target) { dir = goal > target ? 1 : -1; target = goal; }
    p += (target - p) * (1 - Math.exp(-dtMs / 1000 / TAU));
    if (Math.abs(target - p) < 0.004) p = target;

    draw();
    updateOpacity();

    const cur = Math.round(p);
    if (cur !== lastPumpFrame) { lastPumpFrame = cur; pump(); }

    if (p !== target || scrolling || forceDraw) kick();
    else lastTs = 0;
  }

  function kick() {
    if (running || destroyed) return;
    running = true;
    rafId = requestAnimationFrame(tick);
  }

  /* ── events ── */
  const startFollowing = () => {
    if (phase < 3) { phase = 3; pump(); }
  };
  const onScroll = () => {
    lastScrollT = performance.now();
    startFollowing();
    kick();
  };
  let resizeTimer = 0;
  const onResize = () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      measureCanvas();
      measureRange();
      forceDraw = true;
      kick();
      pump();
    }, 120);
  };
  const ro = typeof ResizeObserver === "function" ? new ResizeObserver(() => { measureRange(); kick(); }) : null;

  let idleId = 0;
  let idleTimer = 0;
  const startKeyframes = () => {
    if (destroyed || phase >= 2) return;
    phase = 2;
    pump();
  };
  const onLoad = () => {
    if (typeof window.requestIdleCallback === "function") idleId = window.requestIdleCallback(startKeyframes, { timeout: 2500 });
    else idleTimer = setTimeout(startKeyframes, 1500);
  };

  /* ── start ── */
  measureCanvas();
  measureRange();
  p = target = Math.round(rawFrame());
  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("resize", onResize, { passive: true });
  if (ro) ro.observe(document.body);
  if (document.readyState === "complete") onLoad();
  else window.addEventListener("load", onLoad, { once: true });
  if (window.scrollY > 0) startFollowing();   /* reload or deep link mid-page */
  pump();
  const failTimer = setTimeout(() => { if (!announcedReady) onFail(); }, 8000);
  kick();

  return {
    destroy() {
      destroyed = true;
      cancelAnimationFrame(rafId);
      clearTimeout(resizeTimer);
      clearTimeout(idleTimer);
      clearTimeout(failTimer);
      if (idleId && typeof window.cancelIdleCallback === "function") window.cancelIdleCallback(idleId);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("load", onLoad);
      if (ro) ro.disconnect();
      for (let i = 0; i < N; i++) releaseBitmap(i);
      blobs.fill(null);
    },
  };
}
